// src/pages/EventDetailPage.tsx
import React, { useState, useEffect, Suspense, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { type Event, type Player, type LiveUpdate, type TeamRoster } from '../types';
import { useTranslation } from 'react-i18next';

// --- Komponent pomocniczy: Klikalny gracz ---
const SelectablePlayerItem = ({
    name,
    isSelected,
    onClick,
    isDisabled,
}: { name: string; isSelected: boolean; onClick: () => void; isDisabled: boolean }) => {
    const baseClasses =
        'p-2 border border-gray-600 rounded-md mb-2 transition-colors text-gray-200 cursor-pointer';
    const selectedClasses = 'bg-blue-600 text-white ring-2 ring-blue-400';
    const unselectedClasses = 'bg-gray-700 hover:bg-gray-600';
    const disabledClasses = 'bg-gray-800 text-gray-500 cursor-not-allowed';

    return (
        <div
            onClick={!isDisabled ? onClick : undefined}
            className={`${baseClasses} ${isDisabled ? disabledClasses : isSelected ? selectedClasses : unselectedClasses}`}
        >
            {name}
        </div>
    );
};

// --- Komponent Osi Czasu ---
const VerticalTimeline = ({
    updates,
    sportType,
    status,
    finalScoreA,
    finalScoreB,
}: {
    updates: LiveUpdate[];
    sportType: string;
    status: Event['status'];
    finalScoreA?: number;
    finalScoreB?: number;
}) => {
    const { t } = useTranslation();

    // Sortujemy od najnowszego do najstarszego (jak dotychczas w UI)
    const chronologicalUpdates = [...updates].sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );

    const getIconForUpdate = (update: LiveUpdate) => {
        const updateTypeLower =
            typeof update.updateType === 'string' ? update.updateType.toLowerCase() : '';
        const sportTypeLower = typeof sportType === 'string' ? sportType.toLowerCase() : '';

        switch (updateTypeLower) {
            case 'substitution':
                return '🔄';
            case 'periodend':
                return '🔔';
            case 'matchend': // na przyszłość, gdyby backend wysyłał taki typ
                return '🏁';
            case 'scoreupdate':
                switch (sportTypeLower) {
                    case 'basketball':
                        return '🏀';
                    case 'volleyball':
                        return '🏐';
                    default:
                        return '⚽';
                }
            default:
                return '⏱️';
        }
    };

    const renderNeutralBadge = (icon: string, text: string, extraRight?: React.ReactNode) => (
        <div className="w-full flex justify-center">
            <div className="flex items-center space-x-2 px-3 py-1 rounded-full bg-yellow-800/50">
                <span className="text-lg">{icon}</span>
                <span className="text-white text-xs font-semibold">{text}</span>
                {extraRight}
            </div>
        </div>
    );

    return (
        <div className="w-full bg-gray-900/50 rounded-lg p-4 mt-8">
            <div className="text-center border-b-2 border-gray-700 pb-2 mb-4">
                <span className="text-white font-bold pb-2">{t('eventDetail.timelineTitle')}</span>
            </div>

            <div>
                {/* Syntetyczny wpis: koniec meczu – zawsze na górze listy, gdy status = Finished */}
                {status === 'Finished' && (
                    <div className="flex items-center border-b border-gray-800 py-3 text-sm">
                        <div className="w-2/5" />
                        <div className="w-1/5 text-center font-bold text-gray-400">
                            {renderNeutralBadge(
                                '🏁',
                                t('timeline.endOfMatch') || 'Koniec meczu!',
                                Number.isFinite(finalScoreA) && Number.isFinite(finalScoreB) ? (
                                    <span className="ml-1 text-white text-xs tabular-nums">
                                        {finalScoreA} : {finalScoreB}
                                    </span>
                                ) : undefined
                            )}
                        </div>
                        <div className="w-2/5" />
                    </div>
                )}

                {chronologicalUpdates.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">{t('eventDetail.noEventsYet')}</p>
                ) : (
                    chronologicalUpdates.map((update, index) => {
                        const isTeamA = update.team === 'A';
                        const isNeutral = !update.team;

                        return (
                            <div
                                key={update.id || index}
                                className="flex items-center border-b border-gray-800 py-3 text-sm"
                            >
                                <div className="w-2/5 flex justify-end pr-4 text-right">
                                    {isTeamA && (
                                        <div className="flex items-center gap-x-2 justify-end">
                                            <span className="text-white">{update.comment}</span>
                                            <span className="text-xl">{getIconForUpdate(update)}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="w-1/5 text-center font-bold text-gray-400">
                                    {isNeutral ? (
                                        renderNeutralBadge(getIconForUpdate(update), update.comment || '')
                                    ) : (
                                        <span className="text-gray-500">
                                            {new Date(update.timestamp).toLocaleTimeString('pl-PL', {
                                                hour: '2-digit',
                                                minute: '2-digit',
                                            })}
                                        </span>
                                    )}
                                </div>

                                <div className="w-2/5 flex justify-start pl-4">
                                    {!isTeamA && !isNeutral && (
                                        <div className="flex items-center gap-x-2">
                                            <span className="text-xl">{getIconForUpdate(update)}</span>
                                            <span className="text-white">{update.comment}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

// --- Pomocnicze: rozpoznanie „startu” (nie traktujemy jako koniec części) ---
const isStartLike = (u: LiveUpdate) => {
    const txt = `${u.message || ''} ${u.comment || ''}`.toLowerCase();
    // słowa-klucze: start/początek/rozpocz…
    return /start|pocz(?:ą|a)tek|rozpocz/.test(txt);
};

// --- Główny komponent strony ---
const EventDetailPageContent: React.FC = () => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const [event, setEvent] = useState<Event | null>(null);
    const [connection, setConnection] = useState<HubConnection | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const { id: eventId } = useParams<{ id: string }>();
    const [showAllUpdates, setShowAllUpdates] = useState<boolean>(false);
    const [currentPeriod, setCurrentPeriod] = useState<number>(1);
    const [selectedPlayers, setSelectedPlayers] = useState<{
        team: 'A' | 'B' | null;
        starter: Player | null;
        substitute: Player | null;
    }>({
        team: null,
        starter: null,
        substitute: null,
    });

    const isOwner = event && user ? user.nameid === event.userId : false;

    useEffect(() => {
        if (!eventId) return;

        const fetchEvent = async () => {
            try {
                const response = await api.get(`/Events/${eventId}`);
                const fetchedEvent: Event = response.data;
                fetchedEvent.updates.sort(
                    (a: LiveUpdate, b: LiveUpdate) =>
                        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
                );
                setEvent(fetchedEvent);

                // >>> ZMIANA: zliczamy TYLKO prawdziwe końce części
                const periodEndEvents = fetchedEvent.updates.filter(
                    (u) =>
                        (u.updateType || '').toLowerCase() === 'periodend' &&
                        !isStartLike(u)
                );
                setCurrentPeriod(periodEndEvents.length + 1);
            } catch (err) {
                console.error('Błąd pobierania wydarzenia:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchEvent();

        const origin = process.env.REACT_APP_API_URL
            ? new URL(process.env.REACT_APP_API_URL).origin
            : window.location.origin;
        const hubUrl = origin + '/liveHub';
        const newConnection = new HubConnectionBuilder().withUrl(hubUrl).withAutomaticReconnect().build();
        setConnection(newConnection);

        return () => {
            newConnection.stop();
        };
    }, [eventId]);

    useEffect(() => {
        if (!connection || !eventId) return;

        const startAndListen = async () => {
            if (connection.state === HubConnectionState.Disconnected) {
                try {
                    await connection.start();
                    console.log('Połączono z SignalR!');
                    await connection.invoke('JoinEventGroup', eventId);
                } catch (e) {
                    console.error('Błąd połączenia SignalR: ', e);
                }
            }
        };
        startAndListen();

        const handleNewUpdate = (newUpdate: LiveUpdate) =>
            setEvent((prev) => {
                if (!prev) return null;
                const newUpdates = [newUpdate, ...prev.updates];
                newUpdates.sort(
                    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
                );
                // >>> ZMIANA: zwiększamy okres tylko jeśli to rzeczywisty koniec części
                if (
                    (newUpdate.updateType || '').toLowerCase() === 'periodend' &&
                    !isStartLike(newUpdate)
                ) {
                    setCurrentPeriod((p) => p + 1);
                }
                return { ...prev, updates: newUpdates };
            });

        const handleScoreUpdate = (scoreA: number, scoreB: number) =>
            setEvent((prev) => (prev ? { ...prev, scoreA, scoreB } : null));

        const handleStatusUpdate = (status: Event['status']) =>
            setEvent((prev) => (prev ? { ...prev, status } : null));

        const handleRosterUpdate = (rosters: { rosterA: TeamRoster; rosterB: TeamRoster }) =>
            setEvent((prev) => (prev ? { ...prev, ...rosters } : null));

        connection.on('ReceiveNewUpdate', handleNewUpdate);
        connection.on('ReceiveScoreUpdate', handleScoreUpdate);
        connection.on('ReceiveStatusUpdate', handleStatusUpdate);
        connection.on('ReceiveRosterUpdate', handleRosterUpdate);

        return () => {
            connection.off('ReceiveNewUpdate');
            connection.off('ReceiveScoreUpdate');
            connection.off('ReceiveStatusUpdate');
            connection.off('ReceiveRosterUpdate');
        };
    }, [connection, eventId]);

    useEffect(() => {
        if (!isOwner && eventId && !loading) {
            const intervalId = setInterval(() => {
                api.get(`/Events/${eventId}`).then((response) => {
                    const fetchedEvent = response.data;
                    fetchedEvent.updates.sort(
                        (a: LiveUpdate, b: LiveUpdate) =>
                            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
                    );
                    setEvent(fetchedEvent);
                });
            }, 5000);
            return () => clearInterval(intervalId);
        }
    }, [isOwner, eventId, loading]);

    const postApi = useCallback(
        async (endpoint: string, payload: object) => {
            if (!eventId) return;
            try {
                await api.post(`/Events/${eventId}/${endpoint}`, payload);
            } catch (error) {
                console.error(`Błąd podczas wywoływania ${endpoint}:`, error);
                alert(t('errors.apiError'));
            }
        },
        [eventId, t]
    );

    const handleEndPeriod = useCallback(() => {
        if (!event) return;
        if (currentPeriod >= event.numberOfParts) {
            // kończymy mecz
            postApi('status', { status: 'Finished' });
        } else {
            let message = '';
            const sportTypeLower = (event.sportType || '').toLowerCase();
            switch (sportTypeLower) {
                case 'football':
                    message = t('timeline.endOfHalf', { count: currentPeriod });
                    break;
                case 'basketball':
                    message = t('timeline.endOfQuarter', { count: currentPeriod });
                    break;
                case 'volleyball':
                    message = t('timeline.endOfSet', { count: currentPeriod });
                    break;
                default:
                    message = t('timeline.endOfPeriod', { count: currentPeriod });
            }
            postApi('period', { message });
        }
    }, [event, currentPeriod, postApi, t]);

    const handlePlayerSelect = useCallback(
        (player: Player, team: 'A' | 'B', type: 'starter' | 'substitute') => {
            if (!isOwner || event?.status !== 'InProgress') return;
            if (selectedPlayers.team && selectedPlayers.team !== team) {
                setSelectedPlayers({
                    team,
                    starter: type === 'starter' ? player : null,
                    substitute: type === 'substitute' ? player : null,
                });
            } else {
                setSelectedPlayers((prev) => ({
                    ...prev,
                    team,
                    [type]: prev[type]?.name === player.name ? null : player,
                }));
            }
        },
        [event?.status, isOwner, selectedPlayers.team]
    );

    const handleSubstitution = useCallback(async () => {
        const { team, starter, substitute } = selectedPlayers;
        if (!event || !team || !starter || !substitute) return;

        await postApi('substitution', {
            playerOutName: starter.name,
            playerInName: substitute.name,
            team,
        });

        const newRosters = JSON.parse(
            JSON.stringify({ rosterA: event.rosterA, rosterB: event.rosterB })
        );
        const rosterToUpdate = team === 'A' ? newRosters.rosterA : newRosters.rosterB;
        const starterIndex = rosterToUpdate.starters.findIndex((p: Player) => p.name === starter.name);
        const subIndex = rosterToUpdate.substitutes.findIndex((p: Player) => p.name === substitute.name);
        if (starterIndex !== -1 && subIndex !== -1) {
            [rosterToUpdate.starters[starterIndex], rosterToUpdate.substitutes[subIndex]] = [
                rosterToUpdate.substitutes[subIndex],
                rosterToUpdate.starters[starterIndex],
            ];
            await postApi('rosters', { rosterA: newRosters.rosterA, rosterB: newRosters.rosterB });
        }
        setSelectedPlayers({ team: null, starter: null, substitute: null });
    }, [event, postApi, selectedPlayers]);

    const renderScoreButtons = useCallback(
        (team: 'A' | 'B') => {
            if (!event) return null;
            const isDisabled = event.status !== 'InProgress';
            const buttonClass =
                'px-3 py-1 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-500 disabled:cursor-not-allowed';
            const secondaryButtonClass =
                'px-3 py-1 text-sm font-medium text-gray-200 bg-gray-600 border border-transparent rounded-md shadow-sm hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:bg-gray-500 disabled:cursor-not-allowed';

            const handleUpdate = (points: number) => {
                setEvent((currentEvent) => {
                    if (!currentEvent) return null;
                    const { scoreA, scoreB } = currentEvent;
                    if (team === 'A') {
                        postApi('score', { scoreA: scoreA + points, scoreB });
                    } else {
                        postApi('score', { scoreA, scoreB: scoreB + points });
                    }
                    return currentEvent;
                });
            };

            const sportTypeLower = (event.sportType || '').toLowerCase();
            switch (sportTypeLower) {
                case 'basketball':
                    return (
                        <div className="flex justify-center space-x-2">
                            <button onClick={() => handleUpdate(1)} disabled={isDisabled} className={buttonClass}>
                                +1
                            </button>
                            <button onClick={() => handleUpdate(2)} disabled={isDisabled} className={buttonClass}>
                                +2
                            </button>
                            <button onClick={() => handleUpdate(3)} disabled={isDisabled} className={buttonClass}>
                                +3
                            </button>
                            <button
                                onClick={() => handleUpdate(-1)}
                                disabled={isDisabled}
                                className={secondaryButtonClass}
                            >
                                -1
                            </button>
                        </div>
                    );
                default:
                    return (
                        <div className="flex justify-center space-x-2">
                            <button onClick={() => handleUpdate(1)} disabled={isDisabled} className={buttonClass}>
                                +1
                            </button>
                            <button
                                onClick={() => handleUpdate(-1)}
                                disabled={isDisabled}
                                className={secondaryButtonClass}
                            >
                                -1
                            </button>
                        </div>
                    );
            }
        },
        [event, postApi]
    );

    if (loading) return <p className="text-center p-8 text-white">{t('eventDetail.loading')}</p>;
    if (!event) return <p className="text-center p-8 text-white">{t('eventDetail.notFound')}</p>;

    const displayedUpdates = showAllUpdates ? event.updates : event.updates.slice(0, 5);

    const getPeriodButtonText = () => {
        if (currentPeriod >= event.numberOfParts) return t('eventDetail.endMatch');
        const sportTypeLower = (event.sportType || '').toLowerCase();
        switch (sportTypeLower) {
            case 'football':
                return t('timeline.endOfHalf', { count: currentPeriod });
            case 'basketball':
                return t('timeline.endOfQuarter', { count: currentPeriod });
            case 'volleyball':
                return t('timeline.endOfSet', { count: currentPeriod });
            default:
                return t('timeline.endOfPeriod', { count: currentPeriod });
        }
    };
    const isEndPeriodButtonDisabled = event.status !== 'InProgress';

    return (
        <div className="bg-gray-800 shadow-xl rounded-lg p-6 md:p-8 text-gray-200">
            <div className="text-center">
                <h1 className="text-4xl md:text-5xl font-bold text-white">{event.eventName}</h1>
                <p className="text-lg text-gray-400 mt-2">
                    {t('eventDetail.discipline')}:{' '}
                    {t(`sportTypes.${(event.sportType || '').toLowerCase()}` as const)}
                </p>
                <div className="mt-4 text-lg text-gray-300">
                    <span>
                        {t('eventDetail.status')}: {' '}
                        <span className="font-semibold text-yellow-400">
                            {t(`statuses.${event.status}` as const)}
                        </span>
                    </span>
                    <span className="mx-3">|</span>
                    <span>
                        {t('eventDetail.startTime')}: {new Date(event.startTime).toLocaleString('pl-PL')}
                    </span>
                </div>
            </div>

            <div className="my-8 bg-gray-900 rounded-lg p-6 shadow-inner flex justify-around items-center min-h-[120px]">
                <div className="w-2/5 text-center">
                    <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white break-words">
                        {event.teamAName}
                    </h2>
                </div>
                <div className="text-left px-2">
                    <p className="text-4xl sm:text-5xl md:text-6xl font-black text-yellow-400">
                        {event.scoreA}
                    </p>
                </div>
                <div className="text-right px-2">
                    <p className="text-4xl sm:text-5xl md:text-6xl font-black text-yellow-400">
                        {event.scoreB}
                    </p>
                </div>
                <div className="w-2/5 text-center">
                    <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white break-words">
                        {event.teamBName}
                    </h2>
                </div>
            </div>

            {!isOwner && (
                <p className="text-center text-sm text-gray-500 mb-6 italic">
                    {t('eventDetail.autoRefreshInfo')}
                </p>
            )}

            {isOwner && (
                <div className="operator-panel bg-gray-700/50 p-6 rounded-lg mb-8">
                    <h3 className="text-2xl font-bold text-white mb-6">{t('eventDetail.operatorPanel')}</h3>
                    <div className="space-y-6">
                        <div>
                            <label
                                htmlFor="status-select"
                                className="block text-sm font-medium text-gray-300 mb-2"
                            >
                                {t('eventDetail.changeStatus')}
                            </label>
                            <select
                                id="status-select"
                                value={event.status}
                                onChange={(e) => postApi('status', { status: e.target.value })}
                                className="mt-1 block w-full bg-gray-600 border border-gray-500 rounded-md shadow-sm py-2 px-3 text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="Scheduled">{t('statuses.Scheduled')}</option>
                                <option value="InProgress">{t('statuses.InProgress')}</option>
                                <option value="Finished">{t('statuses.Finished')}</option>
                                <option value="Canceled">{t('statuses.Canceled')}</option>
                            </select>
                        </div>

                        <div className="pt-2">
                            <h4 className="text-lg font-semibold text-center mb-4">{t('eventDetail.scoring')}</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                                <div className="flex flex-col items-center space-y-2">
                                    <h5 className="text-md font-semibold">{event.teamAName}</h5>
                                    {renderScoreButtons('A')}
                                </div>
                                <div className="flex flex-col items-center space-y-2">
                                    <h5 className="text-md font-semibold">{event.teamBName}</h5>
                                    {renderScoreButtons('B')}
                                </div>
                            </div>
                        </div>

                        <div className="pt-4 border-t border-gray-600">
                            <h4 className="text-lg font-semibold text-center mb-4">
                                {t('eventDetail.matchManagement')}
                            </h4>
                            <button
                                onClick={handleEndPeriod}
                                disabled={isEndPeriodButtonDisabled}
                                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:bg-gray-500 disabled:cursor-not-allowed"
                            >
                                {getPeriodButtonText()}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <hr className="border-gray-700 my-8" />

            {event.updates && event.updates.length > 0 && (
                <>
                    <VerticalTimeline
                        updates={displayedUpdates}
                        sportType={event.sportType}
                        status={event.status}
                        finalScoreA={event.scoreA}
                        finalScoreB={event.scoreB}
                    />
                    {event.updates.length > 5 && (
                        <button
                            onClick={() => setShowAllUpdates((prev) => !prev)}
                            className="w-full text-center mt-4 p-2 bg-gray-700 text-gray-200 rounded-lg hover:bg-gray-600 transition-colors"
                        >
                            {showAllUpdates
                                ? t('eventDetail.showLess')
                                : t('eventDetail.showAll', { count: event.updates.length })}
                        </button>
                    )}
                </>
            )}

            <hr className="border-gray-700 my-8" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {[
                    { idPrefix: 'A', name: event.teamAName, roster: event.rosterA },
                    { idPrefix: 'B', name: event.teamBName, roster: event.rosterB },
                ].map((team) => (
                    <div key={team.idPrefix}>
                        <h3 className="text-xl font-bold mb-2">
                            {t('eventDetail.roster')} - {team.name}
                        </h3>

                        <h4 className="mt-4 font-semibold text-lg text-gray-300">
                            {t('createEvent.starters')}:
                        </h4>
                        <div className="p-3 border border-gray-700 rounded-lg bg-gray-800">
                            {isOwner
                                ? team.roster?.starters?.map((p) => (
                                    <SelectablePlayerItem
                                        key={p.name}
                                        name={p.name}
                                        isSelected={
                                            selectedPlayers.team === team.idPrefix &&
                                            selectedPlayers.starter?.name === p.name
                                        }
                                        onClick={() =>
                                            handlePlayerSelect(p, team.idPrefix as 'A' | 'B', 'starter')
                                        }
                                        isDisabled={event.status !== 'InProgress'}
                                    />
                                ))
                                : team.roster?.starters?.map((p) => (
                                    <div key={p.name} className="p-2 text-gray-300">
                                        {p.name}
                                    </div>
                                ))}
                        </div>

                        <h4 className="mt-4 font-semibold text-lg text-gray-300">
                            {t('createEvent.substitutes')}:
                        </h4>
                        <div className="p-3 border border-gray-700 rounded-lg bg-gray-800">
                            {isOwner
                                ? team.roster?.substitutes?.map((p) => (
                                    <SelectablePlayerItem
                                        key={p.name}
                                        name={p.name}
                                        isSelected={
                                            selectedPlayers.team === team.idPrefix &&
                                            selectedPlayers.substitute?.name === p.name
                                        }
                                        onClick={() =>
                                            handlePlayerSelect(p, team.idPrefix as 'A' | 'B', 'substitute')
                                        }
                                        isDisabled={event.status !== 'InProgress'}
                                    />
                                ))
                                : team.roster?.substitutes?.map((p) => (
                                    <div key={p.name} className="p-2 text-gray-300">
                                        {p.name}
                                    </div>
                                ))}
                        </div>

                        {isOwner && (
                            <button
                                onClick={handleSubstitution}
                                disabled={
                                    !(
                                        selectedPlayers.team === team.idPrefix &&
                                        selectedPlayers.starter &&
                                        selectedPlayers.substitute
                                    )
                                }
                                className="mt-4 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-500 disabled:cursor-not-allowed"
                            >
                                {t('eventDetail.makeSubstitution')}
                            </button>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

const EventDetailPage = () => (
    <Suspense fallback={<p className="text-center p-8 text-white">Ładowanie...</p>}>
        <EventDetailPageContent />
    </Suspense>
);

export default EventDetailPage;
